import { List, Map } from 'immutable';

import type { SupportedLang } from 'app/lang';
import type {
  BackendEntitiesDiff,
  BackendMatch, RecordOfBetEntity,
  RecordOfMatchEntity, RecordOfObjectsState,
  RecordOfSportEntity,
  RecordOfStakeEntity,
} from 'app/lib/betting/betting-entities';
import createANTLRNames from 'app/lib/betting/betting-entities/reducers/stakes/utils/create-antlr-names-helper';
import { updateField } from 'app/lib/betting/betting-entities/reducers/utils';

import { BetPresetType } from '../constants';
import {
  BetEntry, BetInfo, EntryAmount, MatchEntity,
  MatchEntry, MatchObjects,
  ObjectEntry, RecordOfBetEntryType,
  RecordOfBetInfoType,
  RecordOfMatchEntityType, RecordOfMatchEntryType,
  RecordOfSportEntryType,
  RecordOfStakeEntryType,
  SportEntry, StakeEntry,
} from '../records';
import { EntriesBets, EntriesStakes } from '../types/entries';
import { PresetWithDisplayType } from '../types/oneClickBet';

// ---

export function createSingleBetInfo(bet: RecordOfBetEntity): RecordOfBetInfoType {
  return BetInfo({
    id: bet.id,
    matchId: bet.matchId,
    amount: EntryAmount(),
  });
}

export function createBetEntry(
  bet: RecordOfBetEntity,
): RecordOfBetEntryType {
  return BetEntry({
    entity: bet,
    price: bet.price,
  });
}

export function createStakeEntry(
  stake: RecordOfStakeEntity,
  bet: RecordOfBetEntity,
): RecordOfStakeEntryType {
  return StakeEntry({
    entity: stake,
    betIds: List.of(bet.id),
  });
}

export function createMatchEntity(
  match: RecordOfMatchEntity,
  matchObjects: MatchObjects,
): RecordOfMatchEntityType {
  return MatchEntity({
    id: match.id,
    sportId: match.sportId,
    tournamentUnionId: match.tournamentUnionId,
    tournamentId: match.tournamentId,
    tournamentStageId: match.tournamentStageId,
    name: match.name,
    isOpen: match.isOpen,
    type: match.type,
    slotType: match.slotType,
    startDate: match.startDate,
    objects: matchObjects,
  });
}

export function createMatchEntry(
  match: RecordOfMatchEntityType,
  bet: RecordOfBetEntity,
): RecordOfMatchEntryType {
  return MatchEntry({
    entity: match,
    betIds: List.of(bet.id),
  });
}

export function createSportEntry(
  sport: RecordOfSportEntity,
  match: RecordOfMatchEntityType,
): RecordOfSportEntryType {
  return SportEntry({
    entity: sport,
    matchIds: List.of(match.id),
  });
}

export const createPreset = (
  { type, value }: Partial<PresetWithDisplayType>,
): PresetWithDisplayType => ({
  type: type ?? BetPresetType.STATIC_VALUE,
  value: value ?? NaN,
});

// ---

export const updateStakesNames = (
  lang: SupportedLang,
  immutableObjects: MatchObjects,
  stakesState: EntriesStakes,
  betsState: EntriesBets,
) => {
  createANTLRNames<EntriesStakes, EntriesBets>(
    lang,
    immutableObjects,
    stakesState,
    betsState,
    (type, id, names) => {
      if (type === 'stake') {
        stakesState = updateField(stakesState, [id, 'entity'], 'names', names);
      }
      if (type === 'bet') {
        betsState = updateField(betsState, [id, 'entity'], 'names', names);
      }
    },
    { getBetEntity, getStakeEntity },
  );

  return {
    stakesState,
    betsState,
  };
};

function getBetEntity(bet: RecordOfBetEntryType): RecordOfBetEntryType['entity'] {
  return bet.entity;
}

function getStakeEntity(stake: RecordOfStakeEntryType): RecordOfStakeEntryType['entity'] {
  return stake.entity;
}

// ---

export function createImmutableObjects(
  match: RecordOfMatchEntity,
  objects: RecordOfObjectsState['byId'],
): MatchObjects {
  return match.objects.reduce((acc: MatchObjects, objectId, teamId) => {
    const object = objects.get(objectId);

    if (object) {
      acc = acc.set(teamId, ObjectEntry(object));
    }

    return acc;
  }, Map());
}

export function createImmutableMatchObjects(
  matchObjects: MatchObjects,
  objects: BackendEntitiesDiff['objects'],
  newMatchObjects?: BackendMatch['objects'],
): MatchObjects {
  if (!objects) {
    return Map();
  }

  return matchObjects.reduce((
    acc,
    matchObject,
    teamId,
  ) => {
    let objectId = matchObject.id;

    if (newMatchObjects) {
      objectId = newMatchObjects[teamId];
    }

    const object = objects[objectId];

    if (!object) {
      acc = acc.set(teamId, matchObject);
      return acc;
    }

    acc = acc.set(teamId, ObjectEntry(object));
    return acc;
  }, Map() as MatchObjects);
}

